import { FormControl, InputLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DateRangePicker } from 'react-dates';
import { Hint } from 'v2/components/Shared/Hint';
import { isSameDay } from 'v2/helpers/date';

import type { FieldProps } from 'formik';
import type { Moment } from 'moment';

const useStyle = makeStyles(() => ({
  form: {
    width: '100% !important',
    position: 'relative',
    zIndex: 20,
  },
  extraButtons: {
    padding: '20px 20px 0 20px',
  },
  button: {
    position: 'relative',
    height: '100%',
    textAlign: 'center',
    background: 'none',
    border: '1px solid #192542',
    padding: '4px 12px',
    marginRight: 8,
    font: 'inherit',
    fontWeight: 100,
    lineHeight: 'normal',
    overflow: 'visible',
    boxSizing: 'border-box',
    cursor: 'pointer',
    color: '#192542',
    borderRadius: 4,
  },
  buttonActive: {
    position: 'relative',
    height: '100%',
    textAlign: 'center',
    background: '#192542',
    border: '1px solid #192542',
    padding: '4px 12px',
    marginRight: 8,
    font: 'inherit',
    fontWeight: 200,
    lineHeight: 'normal',
    overflow: 'visible',
    boxSizing: 'border-box',
    cursor: 'pointer',
    color: '#f3f3f3',
    borderRadius: 4,
  },
  label: {
    padding: '0 0 8px',
  },
}));

interface IValueFromInput {
  startDate: Moment;
  endDate: Moment;
}

const formatDateToString = (date: Moment): string => moment(date).format('DD/MM/YYYY');
const formatStringToDate = (text: string): Moment => moment(text, 'DD/MM/YYYY');

const today = moment();

const presets = [
  {
    id: 'today',
    text: 'Hoje',
    start: today,
    end: today,
  },
  {
    id: 'week',
    text: 'Últimos 7 dias',
    start: moment().subtract(7, 'days'),
    end: today,
  },
  {
    id: '2weeks',
    text: 'Últimos 15 dias',
    start: moment().subtract(15, 'days'),
    end: today,
  },
  {
    id: 'month',
    text: 'Últimos 30 dias',
    start: moment().subtract(30, 'days'),
    end: today,
  },
];

const presetsLonger = [
  {
    id: 'week',
    text: 'Últimos 7 dias',
    start: moment().subtract(7, 'days'),
    end: today,
  },
  {
    id: '1month',
    text: 'Último mês',
    start: moment().subtract(30, 'days'),
    end: today,
  },
  {
    id: '3month',
    text: 'Últimos 3 meses',
    start: moment().subtract(90, 'days'),
    end: today,
  },
  {
    id: '6month',
    text: 'Últimos 6 meses',
    start: moment().subtract(180, 'days'),
    end: today,
  },
];

interface IProps extends FieldProps {
  label?: string;
  isPresetLonger?: boolean;
  isOutsideRange: () => boolean;
  customPreset?: [];
  showPreset?: boolean;
  minDate?: Moment;
  maxDate?: Moment;
}

export const DateRangePickerFormField = ({
  isOutsideRange,
  form,
  label,
  field,
  isPresetLonger,
  customPreset,
  showPreset = true,
  minDate,
  maxDate,
}: IProps) => {
  const [focusedInput, setFocusedInput] = useState<any>();
  const [startDate, setStartDate] = useState<Moment>();
  const [endDate, setEndDate] = useState<Moment>();
  const classes = useStyle({});

  const handleChange = (value: IValueFromInput) => {
    const { startDate, endDate } = value;
    const start = formatDateToString(startDate);
    const end = formatDateToString(endDate);
    if (startDate?.isValid() && endDate?.isValid()) form.setFieldValue(field.name, [start, end]);
  };

  useEffect(() => {
    const v = field.value || [];
    const start = formatStringToDate(v[0]);
    const end = formatStringToDate(v[1]);
    setStartDate(start);
    setEndDate(end);
  }, [field]);

  const preSelectFilter = () => {
    if (!showPreset) {
      return null;
    }

    const preset = isPresetLonger ? presetsLonger : presets;

    return (
      <div className={classes.extraButtons}>
        {(customPreset || preset).map(({ id, text, start, end }) => {
          const isSelected = isSameDay(start, startDate) && isSameDay(end, endDate);
          return (
            <button
              id={`date-range-picker-${id}`}
              className={`${isSelected ? classes.buttonActive : classes.button}`}
              key={text}
              type='button'
              onClick={() => handleChange({ startDate: start, endDate: end })}
            >
              {text}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <Hint inputName={field.name}>
      <FormControl className={classes.form}>
        {label && <InputLabel className={classes.label}>{label}</InputLabel>}

        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          minDate={minDate}
          maxDate={maxDate}
          startDateId='date_start'
          endDateId='date_end'
          onDatesChange={handleChange}
          focusedInput={focusedInput}
          onFocusChange={setFocusedInput}
          displayFormat={'DD/MM/YYYY'}
          small
          startDatePlaceholderText={'Data inicial'}
          endDatePlaceholderText={'Data final'}
          minimumNights={0}
          orientation={'horizontal'}
          enableOutsideDays={false}
          isOutsideRange={isOutsideRange || (() => false)}
          hideKeyboardShortcutsPanel
          renderCalendarInfo={preSelectFilter}
          calendarInfoPosition='top'
          numberOfMonths={window.innerWidth <= 760 ? 1 : 2}
        />
      </FormControl>
    </Hint>
  );
};
